import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import { useNavigate } from 'react-router-dom';

import Row from 'components/Common/Row';
import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import getIdentifier from 'getIdentifier';
import getTimeStamp from 'getTimeStamp';
import Form from 'kingpin/forms/Form';
import FormHeader from 'kingpin/forms/FormHeader';
import FormContent from 'kingpin/forms/FormContent';
import Button from 'kingpin/atoms/Button';
import FormInput from 'kingpin/forms/FormInput';
import TextInput from 'kingpin/atoms/TextInput';
import STORE from 'store';
import useGenerateUrlSlugForName from './useGetWorkspaceUrlSlugForName';
import ToastContext from '../../../contexts/ToastContext';
import WorkspaceTasksToggle from './WorkspaceTasksToggle';

const useNavigationSettings = (workspace?: V3.Workspace) => {
  const sections = useMemo((): V3.Navigation.SideSection[] => {
    return workspace
      ? workspace.navigationSettings.sections
      : [
          {
            key: getIdentifier(),
            title: {
              isEnabled: true,
              label: 'Pinned Assets',
            },
            isBottomBorderEnabled: true,
            items: [],
            sectionSettings: {
              isPinnedSection: true,
              isPeriodFilteredSection: false,
            },
            isAppAdminOnly: false,
          },
          {
            key: getIdentifier(),
            title: { isEnabled: true, label: 'Build' },
            isBottomBorderEnabled: false,
            sectionSettings: {
              isPinnedSection: false,
              isPeriodFilteredSection: false,
            },
            isAppAdminOnly: false,
            items: [
              {
                key: getIdentifier(),
                label: 'All Assets',
                type: 'All Assets',
                icon: 'dashboard',
                dashboards: [],
                scorecards: [],
                reports: [],
                boards: [],
                isAppAdminOnly: false,
              },
              {
                key: getIdentifier(),
                label: 'User Management',
                type: 'Users',
                icon: 'group',
                isAppAdminOnly: true,
              },
            ],
          },
        ];
  }, [workspace]);

  const navigationSettings = useMemo((): V3.Navigation.Config => {
    return workspace
      ? workspace.navigationSettings
      : {
          sections,
          defaultScreen: {
            itemKey: sections[1].items[0].key,
          },
        };
  }, [sections, workspace]);

  return navigationSettings;
};

const WorkspacePopupForm = ({
  close,
  workspace,
}: {
  close: () => void;
  workspace?: V3.Workspace;
}) => {
  const { showToast } = useContext(ToastContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const navigate = useNavigate();
  const [id] = useState<string>(workspace ? workspace.id : getIdentifier());
  const [label, setLabel] = useState<string>(workspace ? workspace.label : '');
  const navigationSettings = useNavigationSettings(workspace);

  const { isUrlSlugValid, urlSlug } = useGenerateUrlSlugForName(label);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const draftWorkspace = useMemo((): V3.Workspace | undefined => {
    if (!label || !urlSlug || !isUrlSlugValid) {
      return undefined;
    }

    return {
      id,
      urlSlug,
      label,
      isHidden: false,
      isPublished: workspace ? workspace.isPublished : false,
      createdBy: workspace ? workspace.createdBy : currentUserId,
      createdOn: workspace ? workspace.createdOn : getTimeStamp(),
      updatedBy: currentUserId,
      updatedOn: getTimeStamp(),
      type: 'Workspace',
      appViewersAccess: workspace
        ? workspace.appViewersAccess
        : {
            isPublic: false,
            groupIds: [],
            userIds: [],
          },
      adminUserIds: workspace ? workspace.adminUserIds : [],
      navigationSettings,
    };
  }, [
    currentUserId,
    id,
    isUrlSlugValid,
    label,
    navigationSettings,
    urlSlug,
    workspace,
  ]);

  const onSaveClicked = useCallback(() => {
    if (draftWorkspace === undefined) {
      return;
    }

    setIsSaving(true);
    STORE.contentDistributions
      .getWorkspacesRef({
        accountId: selectedAccountId,
      })
      .doc(draftWorkspace.id)
      .set(draftWorkspace)
      .then(() => {
        showToast(`Workspace ${workspace ? 'updated' : 'created'}!`);
        setIsSaving(false);
        navigate(`/workspaces/${draftWorkspace.urlSlug}`);
      });
  }, [draftWorkspace, navigate, selectedAccountId, showToast, workspace]);

  return (
    <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
      <Form>
        <FormHeader
          title={`${workspace ? 'Update' : 'Create'} Workspace`}
          onClose={close}
        />
        <FormContent>
          <FormInput label={'Name'}>
            <TextInput
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
              }}
            />
          </FormInput>
          {workspace && <WorkspaceTasksToggle workspace={workspace} />}
        </FormContent>
        <Row spaceBetween centerAlign>
          <Button
            size={'Small'}
            type={'Secondary'}
            label={'Cancel'}
            onClick={close}
          />
          <Button
            size={'Small'}
            type={'Primary'}
            label={workspace ? 'Update' : 'Create'}
            onClick={onSaveClicked}
            isLoading={isSaving}
            isDisabled={isSaving || !draftWorkspace}
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default WorkspacePopupForm;
